import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import {
  OptionRow,
  getRecommendedSlot,
  AssignedResourcePanel,
  AssignedResourceNote,
  SheetSearchField,
  ALL_DURATIONS,
  DurationChips,
  groupDurationOptions,
  filterGroupByDuration,
  useGroupDuration
} from "/imports/ui/components/booking";
import { groupSlotsByLocation } from "../lessonHelpers";

// Show the trainer search only when the list is long enough that scanning it
// is slower than typing.
const TRAINER_SEARCH_MIN = 6;

const matchesSearch = (name, q) => String(name || "").toLowerCase().includes(q);

// Bottom sheet shared by the filter pickers and the multi-option picker.
function LessonFilterSheet({ lesson }) {
  const {
    currency,
    filters,
    setFilter,
    setFilters,
    availableSports,
    sportFilteredServices,
    availableTrainers,
    allowTrainerPick,
    durationOptions,
    serviceLocations,
    expandedGroup,
    activeSheet,
    closeSheet,
    handleSportChange,
    handleSlotSelect,
    sheetTitles
  } = lesson;

  // Trainer-name search, shared by the trainer filter and the per-slot options
  // picker. Reset whenever a different sheet opens.
  const [search, setSearch] = useState("");
  useEffect(() => {
    setSearch("");
  }, [activeSheet]);
  const query = search.trim().toLowerCase();

  // Several lengths at the expanded time ("All durations"): the options sheet
  // leads with a chip strip and narrows the rows to one length.
  const [groupDuration, setGroupDuration] = useGroupDuration(expandedGroup);

  const renderSheetBody = () => {
    switch (activeSheet) {
      case "sport":
        return availableSports.map((s) => (
          <OptionRow
            key={s._id}
            label={s.name}
            selected={filters.sport === s._id}
            onClick={() => {
              handleSportChange(s._id);
              closeSheet();
            }}
          />
        ));
      case "service":
        return sportFilteredServices.map((s) => (
          <OptionRow
            key={s._id}
            label={s.name}
            selected={filters.serviceId === s._id}
            onClick={() => {
              setFilters({ serviceId: s._id, trainerId: null, duration: "" });
              closeSheet();
            }}
          />
        ));
      case "trainer": {
        if (!allowTrainerPick) return <AssignedResourcePanel noun="Trainer" names={availableTrainers.map((t) => t.name)} />;
        const visibleTrainers = query ? availableTrainers.filter((t) => matchesSearch(t.name, query)) : availableTrainers;
        return (
          <>
            {availableTrainers.length >= TRAINER_SEARCH_MIN && (
              <SheetSearchField value={search} onChange={setSearch} placeholder="Search trainers..." />
            )}
            {!query && (
              <OptionRow
                label="Any Trainer"
                selected={!filters.trainerId}
                onClick={() => {
                  setFilter("trainerId", null);
                  closeSheet();
                }}
              />
            )}
            {visibleTrainers.map((t) => (
              <OptionRow
                key={t._id}
                label={t.name}
                sublabel={t.title}
                selected={filters.trainerId === t._id}
                onClick={() => {
                  setFilter("trainerId", t._id);
                  closeSheet();
                }}
              />
            ))}
            {query && visibleTrainers.length === 0 && (
              <Typography sx={{ px: 1, py: 2, fontSize: "0.85rem", color: "text.secondary", textAlign: "center" }}>No matching trainers</Typography>
            )}
          </>
        );
      }
      case "duration":
        return (
          <>
            {durationOptions.length > 1 && (
              <OptionRow
                label="All durations"
                sublabel="Pick a time first, then the length"
                selected={filters.duration === ALL_DURATIONS}
                onClick={() => {
                  setFilter("duration", ALL_DURATIONS);
                  closeSheet();
                }}
              />
            )}
            {durationOptions.map((opt) => {
              const label = opt.price
                ? `${opt.value} min — ${opt.isFrom ? "from " : ""}${(currency || "").toUpperCase()} ${opt.price}`
                : `${opt.value} min`;
              return (
                <OptionRow
                  key={opt.value}
                  label={label}
                  selected={filters.duration === String(opt.value)}
                  onClick={() => {
                    setFilter("duration", String(opt.value));
                    closeSheet();
                  }}
                />
              );
            })}
          </>
        );
      case "location":
        return (
          <>
            {serviceLocations.length > 1 && (
              <OptionRow
                label="All Locations"
                selected={!filters.locationId}
                onClick={() => {
                  setFilter("locationId", null);
                  closeSheet();
                }}
              />
            )}
            {serviceLocations.map((l) => (
              <OptionRow
                key={l._id}
                label={l.name}
                selected={filters.locationId === l._id}
                onClick={() => {
                  setFilter("locationId", l._id);
                  closeSheet();
                }}
              />
            ))}
          </>
        );
      case "options": {
        const groupDurations = groupDurationOptions(expandedGroup);
        const group = filterGroupByDuration(expandedGroup, groupDuration);
        const freeSlots = group?.slots || [];
        const anySlot = freeSlots.length ? getRecommendedSlot(group) : null;
        const locationGroups = groupSlotsByLocation(freeSlots);
        const pickLocation = !allowTrainerPick && locationGroups.length > 1;
        const trainerSlots = freeSlots;
        const visibleSlots = query ? trainerSlots.filter((s) => matchesSearch(s.trainerName, query)) : trainerSlots;
        return (
          <>
            <DurationChips options={groupDurations} value={groupDuration} onChange={setGroupDuration} currency={currency} />
            {allowTrainerPick && trainerSlots.length >= TRAINER_SEARCH_MIN && (
              <SheetSearchField value={search} onChange={setSearch} placeholder="Search trainers..." />
            )}

            {!pickLocation && anySlot && !query && (
              <OptionRow label="Any Trainer" sublabel="Recommended — best availability" selected onClick={() => handleSlotSelect(anySlot)} />
            )}

            {pickLocation &&
              locationGroups.map((group) => {
                const slot = getRecommendedSlot(group);
                return (
                  <OptionRow
                    key={group.locationId}
                    label={group.locationName}
                    sublabel={`${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`}
                    onClick={() => handleSlotSelect(slot)}
                  />
                );
              })}

            {!allowTrainerPick && <AssignedResourceNote noun="Trainer" />}

            {allowTrainerPick &&
              visibleSlots.map((slot) => (
                <OptionRow
                  key={`${slot.trainerId}-${slot.locationId}-${slot.pricingId}`}
                  label={slot.trainerName}
                  sublabel={[
                    !filters.locationId && slot.locationName,
                    `${(slot.currency || currency || "").toUpperCase()} ${parseFloat(slot.price || 0).toFixed(2)}`
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                  onClick={() => handleSlotSelect(slot)}
                />
              ))}
            {allowTrainerPick && query && visibleSlots.length === 0 && (
              <Typography sx={{ px: 1, py: 2, fontSize: "0.85rem", color: "text.secondary", textAlign: "center" }}>No matching trainers</Typography>
            )}
          </>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={!!activeSheet}
      onClose={closeSheet}
      PaperProps={{ sx: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "75vh", px: 2.5, pt: 2, pb: 3 } }}
    >
      <Box sx={{ width: "2.5rem", height: "0.25rem", borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2 }} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700 }}>{sheetTitles[activeSheet] || ""}</Typography>
        <IconButton size="small" onClick={closeSheet} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, overflowY: "auto" }}>{renderSheetBody()}</Box>
    </Drawer>
  );
}

export default LessonFilterSheet;
